/**
 * Page: EventListPage
 * Main events list page - orchestrates hooks and renders template
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEventList, useEvent } from '../hooks';
import { EventListTemplate } from '../templates';
import { PAGINATION } from '@/constants';
import { usePermissions } from '@/hooks/usePermissions';
import type { Event, EventStatus } from '@/types/event.types';

export function EventListPage() {
  const navigate = useNavigate();

  // Permissions (for now, assume ADMIN role can edit)
  // TODO: Implement proper permissions hook
  const { can } = usePermissions();
  const canEdit = can('event.edit');

  // Load events list
  const {
    events,
    isLoading,
    filters,
    pagination,
    updateFilters,
    changePage,
    changePageSize,
    reload,
  } = useEventList();

  // Event CRUD operations
  const { deleteEvent, isDeleting } = useEvent();

  // Delete confirmation state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState<Event | null>(null);

  // Handle create new
  const handleCreateNew = () => {
    navigate('/events/new');
  };

  // Handle view event
  const handleView = (event: Event) => {
    navigate(`/events/${event.id}`);
  };

  // Handle edit event
  const handleEdit = (event: Event) => {
    navigate(`/events/${event.id}/edit`);
  };

  // Handle delete click
  const handleDeleteClick = (event: Event) => {
    setDeletingEvent(event);
    setShowDeleteDialog(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!deletingEvent) return;

    const success = await deleteEvent(deletingEvent.id);
    if (success) {
      setShowDeleteDialog(false);
      setDeletingEvent(null);
      reload(); // Reload list after deletion
    }
  };

  // Handle close delete dialog
  const handleCloseDeleteDialog = () => {
    if (!isDeleting) {
      setShowDeleteDialog(false);
      setDeletingEvent(null);
    }
  };

  return (
    <EventListTemplate
      // Data
      events={events}
      // State
      isLoading={isLoading}
      canEdit={canEdit}
      // Pagination
      currentPage={pagination.page}
      totalPages={pagination.totalPages}
      totalItems={pagination.totalItems}
      pageSize={pagination.pageSize}
      pageSizeOptions={PAGINATION.PAGE_SIZE_OPTIONS}
      onPageChange={changePage}
      onPageSizeChange={changePageSize}
      // Search & Filters
      searchValue={filters.search}
      onSearchChange={(value) => updateFilters({ search: value })}
      statusFilter={filters.status}
      onStatusFilterChange={(value) =>
        updateFilters({ status: value as EventStatus | 'all' })
      }
      dateFrom={filters.startDateFrom}
      dateTo={filters.startDateTo}
      onDateRangeChange={(from, to) => updateFilters({ startDateFrom: from, startDateTo: to })}
      // Actions
      onView={handleView}
      onEdit={handleEdit}
      onDelete={handleDeleteClick}
      onCreateNew={handleCreateNew}
      // Delete confirmation
      showDeleteDialog={showDeleteDialog}
      onCloseDeleteDialog={handleCloseDeleteDialog}
      onConfirmDelete={handleConfirmDelete}
      deletingEvent={deletingEvent}
      isDeleting={isDeleting}
    />
  );
}
